import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Paper,
  CircularProgress,
  Link,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { jwtDecode } from "jwt-decode";
import { ethers } from "ethers";
import { useGetNonceQuery, useVerifyAndLinkMutation, useDissconnectMutation } from "../../redux/api/authApi";
import { authStorageGet, authStorageSet, authStorageRemove } from "../../utils/authStorage";
import { INVESTOR_WALLET_STORAGE_KEYS } from "./investorWalletStorageKeys";

const PUBLIC_RPC =
  process.env.REACT_APP_WALLET_RPC_URL || "https://rpc.ankr.com/eth";
const ETH_CHAIN_ID = Number(process.env.REACT_APP_WALLET_CHAIN_ID) || 1;
const WC_PROJECT_ID =
  process.env.REACT_APP_WALLETCONNECT_PROJECT_ID ||
  "471820b2c7ee46286261bc8218566e35";

const TEAL = "#0d9488";

// function truncateAddress(a) {
//   if (!a) return "";
//   const s = String(a);
//   return s.length > 10 ? `${s.slice(0, 6)}...${s.slice(-4)}` : s;
// }

function getUserIdFromToken() {
  try {
    const token = authStorageGet("token");
    if (!token) return null;
    const d = jwtDecode(token);
    return d?.id ?? d?.userId ?? d?.sub ?? null;
  } catch {
    return null;
  }
}

const walletRowSx = {
  border: "1px solid #e5e7eb",
  p: 2,
  borderRadius: 2,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  transition: "border-color 0.2s, box-shadow 0.2s",
  "&:hover": { borderColor: TEAL, boxShadow: "0 1px 8px rgba(13,148,136,0.12)" },
};

export default function InvestorConnectWalletDialog({
  open,
  onClose,
  onLinked,
  storageKeys = INVESTOR_WALLET_STORAGE_KEYS,
}) {
  const { data: nonceData, refetch: refetchNonce } = useGetNonceQuery(undefined, {
    skip: !open,
  });
  const [verifyAndLink] = useVerifyAndLinkMutation();

  const [ui, setUi] = useState("select");
  const [connectingLabel, setConnectingLabel] = useState("");
  const [successProvider, setSuccessProvider] = useState("");

  useEffect(() => {
    if (!open) return;
    setUi("select");
    setConnectingLabel("");
    setSuccessProvider("");
  }, [open]);

  const linkWalletFlow = async (externalProvider, providerName) => {
    let nonce =
      nonceData?.data?.nonce ??
      nonceData?.nonce ??
      nonceData?.data?.data?.nonce;
    if (nonce == null || nonce === "") {
      const refetched = await refetchNonce();
      const d = refetched?.data;
      nonce = d?.data?.nonce ?? d?.nonce ?? d?.data?.data?.nonce;
    }
    if (nonce == null || nonce === "") {
      throw new Error("Could not load signing nonce. Check wallet API.");
    }

    const provider = new ethers.BrowserProvider(externalProvider);
    const signer = await provider.getSigner();
    const userAddress = await signer.getAddress();
    const message = String(nonce);
    const signature = await signer.signMessage(message);

    let userId = authStorageGet("userId") || getUserIdFromToken();
    if (!userId) throw new Error("User ID not found. Please sign in again.");

    await verifyAndLink({
      userId: String(userId),
      address: userAddress,
      signature,
      message,
    }).unwrap();

    authStorageSet(storageKeys.address, userAddress);
    authStorageSet(storageKeys.provider, providerName);

    setSuccessProvider(providerName);
    setUi("success");
    if (typeof onLinked === "function") {
      onLinked({ address: userAddress, provider: providerName });
    }
  };

  const runConnect = async (fn, label) => {
    try {
      setConnectingLabel(label);
      setUi("connecting");
      await fn();
    } catch (e) {
      console.error(e);
      setUi("select");
    }
  };

  const handleMetamask = () =>
    runConnect(async () => {
      if (!window.ethereum) {
        window.open("https://metamask.io/", "_blank");
        throw new Error("MetaMask is not installed.");
      }
      await window.ethereum.request({ method: "eth_requestAccounts" });
      await linkWalletFlow(window.ethereum, "MetaMask");
    }, "MetaMask");

  const handleWalletConnect = () =>
    runConnect(async () => {
      const { default: EthereumProvider } = await import(
        "@walletconnect/ethereum-provider"
      );
      const wcProvider = await EthereumProvider.init({
        projectId: WC_PROJECT_ID,
        chains: [ETH_CHAIN_ID],
        showQrModal: true,
        rpcMap: { [ETH_CHAIN_ID]: PUBLIC_RPC },
      });
      await wcProvider.connect();
      await linkWalletFlow(wcProvider, "WalletConnect");
    }, "WalletConnect");

  const handleCoinbase = () =>
    runConnect(async () => {
      const { default: CoinbaseWalletSDK } = await import("@coinbase/wallet-sdk");
      const cb = new CoinbaseWalletSDK({
        appName: "Tokenitize",
        appLogoUrl: `${window.location.origin}/logo192.png`,
        darkMode: false,
      });
      const coinbaseProvider = cb.makeWeb3Provider(PUBLIC_RPC, ETH_CHAIN_ID);
      await coinbaseProvider.request({ method: "eth_requestAccounts" });
      await linkWalletFlow(coinbaseProvider, "Coinbase Wallet");
    }, "Coinbase Wallet");

  const handleClose = () => {
    if (ui === "connecting") return;
    onClose();
  };

  const handleDone = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 2.5, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountBalanceWalletOutlined sx={{ color: TEAL, fontSize: 28 }} />
          <Box>
            <Typography fontWeight={800} fontSize={20}>
              Connect Your Wallet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Choose a wallet provider to connect and manage your digital assets
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} size="small" aria-label="Close" disabled={ui === "connecting"}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5 }}>
        {ui === "select" && (
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              onClick={handleMetamask}
              sx={walletRowSx}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "#fff7ed",
                    backgroundImage: "url(https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg)",
                    backgroundSize: "28px 28px",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography fontWeight={700}>MetaMask</Typography>
                    <Chip label="Popular" size="small" sx={{ height: 22, fontSize: 11, bgcolor: "#e0f2fe", color: "#0369a1" }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Connect using MetaMask browser extension
                  </Typography>
                </Box>
              </Stack>
              <LaunchIcon sx={{ color: "#94a3b8", flexShrink: 0 }} />
            </Paper>

            <Paper elevation={0} onClick={handleWalletConnect} sx={walletRowSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "#dbeafe",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    fontWeight: 800,
                    color: "#2563eb",
                    fontSize: 12,
                  }}
                >
                  WC
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography fontWeight={700}>WalletConnect</Typography>
                    <Chip label="Popular" size="small" sx={{ height: 22, fontSize: 11, bgcolor: "#e0f2fe", color: "#0369a1" }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Scan with your mobile wallet
                  </Typography>
                </Box>
              </Stack>
              <LaunchIcon sx={{ color: "#94a3b8", flexShrink: 0 }} />
            </Paper>

            <Paper elevation={0} onClick={handleCoinbase} sx={walletRowSx}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: "#0052ff",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 11,
                  }}
                >
                  CB
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={700}>Coinbase Wallet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connect using Coinbase Wallet extension or app
                  </Typography>
                </Box>
              </Stack>
              <LaunchIcon sx={{ color: "#94a3b8", flexShrink: 0 }} />
            </Paper>

            <Paper
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: "#e0f2fe",
                border: "1px solid #bae6fd",
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <InfoOutlined sx={{ color: "#0369a1", mt: 0.25 }} />
              <Box>
                <Typography fontWeight={700} color="#0c4a6e" fontSize={14}>
                  Secure connection
                </Typography>
                <Typography variant="caption" color="#0369a1" sx={{ display: "block", mt: 0.5, lineHeight: 1.4 }}>
                  We never store your private keys. Your wallet connection is encrypted and secure.
                </Typography>
              </Box>
            </Paper>

            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ pt: 1 }}>
              New to wallets?{" "}
              <Link href="https://ethereum.org/en/wallets/" target="_blank" rel="noopener noreferrer" fontWeight={600}>
                Learn how to get started
              </Link>
            </Typography>
          </Stack>
        )}

        {ui === "connecting" && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <Box sx={{ position: "relative", width: 72, height: 72 }}>
              <CircularProgress size={72} thickness={4} sx={{ color: TEAL }} />
            </Box>
            <Typography fontWeight={800}>Connecting to {connectingLabel}</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Please approve the connection in your wallet…
            </Typography>
          </Stack>
        )}

        {ui === "success" && (
          <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#dcfce7",
                display: "grid",
                placeItems: "center",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48, color: "#16a34a" }} />
            </Box>
            <Typography fontWeight={800} fontSize={20}>
              Wallet connected successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Your {successProvider} wallet is now linked to your account.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={handleDone}
              sx={{
                mt: 1,
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                borderRadius: 2,
                "&:hover": { bgcolor: "#0f766e" },
              }}
            >
              Done
            </Button>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}

export function useDisconnectLinkedWallet(
  storageKeys = INVESTOR_WALLET_STORAGE_KEYS
) {
  const [dissconnect] = useDissconnectMutation();
  return async (address) => {
    if (address) {
      try {
        await dissconnect({ address }).unwrap();
      } catch (e) {
        console.warn("Disconnect API:", e);
      }
    }
    authStorageRemove(storageKeys.address);
    authStorageRemove(storageKeys.provider);
  };
}
